import { getConfigForTest } from 'Components/Worksheets/Config';
import { Epic, ofType } from 'redux-observable';
import { of } from 'rxjs';
import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { actions } from '.';
import webApi from '../../../Lib/WebApi';
import { addErrorNotice, callApi, catchWithAuthErrors } from '../../Helpers/rxjsHelpers';
import { getDefaultWorksheetStatusPatch, getWorksheetStatusPatch } from '../../Helpers/worksheetHelpers';
import { syncAndAwaitResolve } from '../DataSync';
import * as userActions from '../User';
import { callApiGetWorksheet, callApiPostImportWorksheet, callApiPutTestedByCheckedBy } from './apiCalls';

export const init: Epic = (action$) =>
  action$.pipe(
    ofType(actions.init.toString()),
    mergeMap(({ payload: { worksheetId = null } = {} }: any) =>
      callApi(webApi.worksheets.type.get({ worksheetId })).pipe(
        map((type: any) => type.Body),
        mergeMap((type) =>
          callApiGetWorksheet(type.WorksheetType, worksheetId, true).pipe(
            mergeMap((worksheet) =>
              of(
                actions.initResolve({
                  worksheetType: type,
                  worksheet,
                  config: type?.Id && getConfigForTest ? getConfigForTest(type.Id) : null,
                })
              )
            ),
            catchWithAuthErrors((err: any) =>
              of(actions.initReject(), addErrorNotice('Failed to initialise worksheet.', err))
            )
          )
        ),
        catchWithAuthErrors((err: any) =>
          of(actions.initReject(), addErrorNotice('Failed to initialise worksheet.', err))
        )
      )
    )
  );

export const importWorksheet: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.import.toString()),
    // TODO: Imports for the same worksheetId should be debounced. See QP-3972.
    concatMap(({ payload: { worksheetId = null, importModel = null } = {} }) =>
      callApiPostImportWorksheet({ Body: importModel }, { worksheetId }).pipe(
        mergeMap(() => syncAndAwaitResolve(action$, of(actions.importResolve()))),
        catchWithAuthErrors((error: any) =>
          of(
            addErrorNotice('Importing reference data failed.', error),
            actions.sync({ force: true }),
            actions.importReject()
          )
        )
      )
    )
  );

export const updateStatusValues: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      // Need to update status values whenever local values change (via input or via api response)
      actions.sync.toString(),
      actions.syncResolve.toString(),
      actions.init.toString(),
      actions.initResolve.toString(),
      actions.import.toString(),
      actions.importResolve.toString(),
      actions.importReject.toString(),
      actions.syncErrorResolve.toString(),
      actions.setTestedByCheckedBy.toString(),
      actions.setTestedByCheckedByResolve.toString(),
      actions.setTestedByCheckedByReject.toString(),
      actions.deleteWorksheet.toString(),
      actions.deleteWorksheetResolve.toString(),
      actions.deleteWorksheetReject.toString(),
      userActions.actions.getUser.toString(),
      userActions.actions.getUserResolve.toString(),
      userActions.actions.getUserReject.toString()
    ),
    switchMap(() => {
      if (!state$.value.worksheetEditor.status.ready) {
        // Reset calculated status to default
        return of(actions.patchStatus({ status: getDefaultWorksheetStatusPatch() }));
      }

      const {
        value: {
          worksheetEditor: { syncModel: worksheet, status },
          user,
        },
      } = state$;

      return of(actions.patchStatus({ status: getWorksheetStatusPatch(worksheet, user, status) }));
    })
  );

export const setTestedByCheckedBy: Epic = (action$, state$) =>
  action$.pipe(
    ofType(actions.setTestedByCheckedBy.toString()),
    concatMap(({ payload: { type = null, worksheetId = null, model = null } = {} }) => {
      return callApiPutTestedByCheckedBy(type, { Body: model }, { worksheetId }).pipe(
        mergeMap(() => of(actions.setTestedByCheckedByResolve(), actions.sync({ force: true }))),
        catchWithAuthErrors((error: any) =>
          of(
            addErrorNotice('Setting ' + type + ' failed.', error),
            actions.setTestedByCheckedByReject(),
            actions.sync({ force: true })
          )
        )
      );
    })
  );

export const deleteWorksheet: Epic = (action$, state$) => {
  return action$.pipe(
    ofType(actions.deleteWorksheet.toString()),
    switchMap(({ payload: { worksheetId = state$.value.worksheetEditor.worksheetId } = {} }) => {
      return callApi(
        webApi.worksheets.delete({
          worksheetId,
        })
      ).pipe(
        map(() => actions.deleteWorksheetResolve()),
        catchWithAuthErrors((err: any) =>
          of(actions.deleteWorksheetReject(), addErrorNotice('Failed to delete worksheet.', err))
        )
      );
    })
  );
};

export const uploadImageAttachment: Epic = (action$) => {
  return action$.pipe(
    ofType(actions.uploadImageAttachment.toString()),
    switchMap(({ payload: { worksheetId, imageId, binaryData, fileName } }) => {
      return callApi(
        webApi.worksheets.uploadImageAttachment.put(
          { Body: { BinaryData: binaryData, FileName: fileName } },
          { worksheetId, imageId }
        )
      ).pipe(
        mergeMap(() => of(actions.uploadImageAttachmentResolve(), actions.sync({ force: true }))),
        catchWithAuthErrors((err: any) =>
          of(actions.uploadImageAttachmentReject(), addErrorNotice('Failed to upload image.', err))
        )
      );
    })
  );
};
